import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { InspectArtifactModal } from './InspectArtifactModal';

interface InspectArtifactButtonProps {
  artifactId: string;
  artifactType: ArtifactType;
  matterId?: string;
  /**
   * Optional custom button className. Ignored if `compact` is true and no custom class provided.
   */
  buttonClassName?: string;
  /**
   * Optional custom icon to render. If not provided, a default magnifying glass icon is used.
   * When `compact` is true a smaller default icon is used.
   */
  icon?: React.ReactNode;
  label?: string;
  /** Render a more compact version (smaller padding, smaller icon) */
  compact?: boolean;
}

export const InspectArtifactButton = ({
  artifactId,
  artifactType,
  matterId: matterIdProp,
  buttonClassName,
  icon,
  label,
  compact = false,
}: InspectArtifactButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { matter_id: matterIdFromParams } = useParams<{ matter_id: string }>();

  // Use provided matterId or get from URL params
  const matterId = matterIdProp || matterIdFromParams || '';

  // Determine effective classes & icon
  const effectiveButtonClass =
    buttonClassName ??
    (compact
      ? 'p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors'
      : 'p-1.5 rounded hover:bg-gray-200 transition');

  const effectiveIcon =
    icon ??
    (compact ? (
      <MagnifyingGlassIcon className="h-3.5 w-3.5" />
    ) : (
      <MagnifyingGlassIcon className="h-4 w-4" />
    ));

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Inspect"
          className={effectiveButtonClass}
        >
          {effectiveIcon}
        </button>
      ) : (
        <Button
          plain
          onClick={() => setIsOpen(true)}
          aria-label="Inspect"
          className={effectiveButtonClass}
        >
          {effectiveIcon}
          {label && <span className="ml-1">{label}</span>}
        </Button>
      )}

      <InspectArtifactModal
        artifactId={artifactId}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      />
    </>
  );
};
